<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { vOnClickOutside } from '@vueuse/components';
import format from 'date-fns/format';
import fromUnixTime from 'date-fns/fromUnixTime';
import parseISO from 'date-fns/parseISO';

import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAlert } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  hasPermissions,
  getUserPermissions,
} from 'dashboard/helper/permissionsHelper';
import { REPORTS_OVERRIDE_PERMISSIONS } from 'dashboard/constants/permissions';
import ConversationApi from 'dashboard/api/inbox/conversation';
import ReportingOverridesAPI from 'dashboard/api/reportingOverrides';
import ConversationBox from 'dashboard/components/widgets/conversation/ConversationBox.vue';
import ConversationSidebar from 'dashboard/components/widgets/conversation/ConversationSidebar.vue';
import SidepanelSwitch from 'dashboard/components-next/Conversation/SidepanelSwitch.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CompactDateInboxFilters from '../settings/reports/components/CompactDateInboxFilters.vue';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { uiSettings, updateUISettings } = useUISettings();
const { t, locale } = useI18n();

const currentChat = useMapGetter('getSelectedChat');
const conversationById = useMapGetter('getConversationById');
const currentUser = useMapGetter('getCurrentUser');
const currentAccountId = useMapGetter('getCurrentAccountId');
const archiveFilters = ref({
  inboxId: null,
  userIds: [],
  labelId: null,
  teamId: null,
  vipLevelId: null,
  issueId: null,
  priority: null,
});

const conversations = ref([]);
const totalCount = ref(0);
const hasTotalCount = ref(false);
const missedSessionsPerConversation = ref({});
const missedSessionAssignees = ref({});
const missedWindows = ref([]);
const totalMissedSessions = ref(0);
const excludedCount = ref(0);
const page = ref(1);
const hasMore = ref(false);
const isLoadingList = ref(false);
const isFetchingMore = ref(false);
const listError = ref('');
const showFilterMenu = ref(false);
const isViewActive = ref(true);
const latestListRequestToken = ref(0);
const overridesByConversationId = ref({});
const overrideLoadingIds = ref({});

const RESULTS_PER_PAGE = 25;

const searchInput = ref('');
const searchQuery = ref('');
const sortOrder = ref('last_activity_at_desc');

let archiveFilterRefreshTimeoutId = null;

const selectedConversationId = computed(() =>
  Number(route.params.conversationId || 0)
);
const reportFrom = computed(() => Number(route.query.from || 0));
const reportTo = computed(() => Number(route.query.to || 0));
const archiveFilter = computed(() => String(route.query.archive_filter || ''));

const hasDateFilter = computed(
  () => reportFrom.value > 0 && reportTo.value > 0
);
const isMissedChatsArchive = computed(
  () => archiveFilter.value === 'missed_chats'
);
const isExcludedFromKpiArchive = computed(
  () => archiveFilter.value === 'excluded_from_kpi'
);
const canExcludeFromMissedChats = computed(() => {
  if (!isMissedChatsArchive.value && !isExcludedFromKpiArchive.value)
    return false;
  const permissions = getUserPermissions(
    currentUser.value,
    currentAccountId.value
  );
  return hasPermissions(
    ['administrator', REPORTS_OVERRIDE_PERMISSIONS],
    permissions
  );
});
const isCsatRatedGoodArchive = computed(
  () => archiveFilter.value === 'csat_rated_good'
);
const isCsatRatedBadArchive = computed(
  () => archiveFilter.value === 'csat_rated_bad'
);
const isCsatArchive = computed(
  () => isCsatRatedGoodArchive.value || isCsatRatedBadArchive.value
);
const isVipChatsArchive = computed(() => archiveFilter.value === 'vip_chats');
const archiveConversationType = computed(() => {
  if (isMissedChatsArchive.value) return 'missed_chats_archive';
  if (isExcludedFromKpiArchive.value) return 'excluded_from_kpi_archive';
  if (isCsatRatedGoodArchive.value) return 'csat_rated_good_archive';
  if (isCsatRatedBadArchive.value) return 'csat_rated_bad_archive';
  if (isVipChatsArchive.value) return 'vip_chats_archive';
  return undefined;
});
const showAllArchivedChats = computed(() => !archiveConversationType.value);
const archiveBackLabel = computed(() => {
  if (isMissedChatsArchive.value) return t('ARCHIVES.BACK_TO_MISSED_CHATS');
  if (isExcludedFromKpiArchive.value) return t('ARCHIVES.BACK_TO_MISSED_CHATS');
  if (isCsatArchive.value) return t('ARCHIVES.BACK_TO_CHAT_SATISFACTION');
  return '';
});
const sortLabel = computed(() =>
  sortOrder.value === 'last_activity_at_asc'
    ? t('ARCHIVES.SORT_OLDEST')
    : t('ARCHIVES.SORT_NEWEST')
);
const countLabel = computed(() => {
  const count = Number(totalCount.value || 0);
  let suffix = '';
  if (!hasTotalCount.value && hasMore.value) {
    suffix = '+';
  }
  const base = `${count.toLocaleString()}${suffix} ${t('ARCHIVES.CHATS_SUFFIX')}`;
  const segments = [base];
  if (
    (isMissedChatsArchive.value || isExcludedFromKpiArchive.value) &&
    totalMissedSessions.value > 0
  ) {
    segments.push(
      `${totalMissedSessions.value.toLocaleString()} ${t('ARCHIVES.MISSED_SESSIONS_SUFFIX')}`
    );
  }
  if (
    (isMissedChatsArchive.value || isExcludedFromKpiArchive.value) &&
    excludedCount.value > 0
  ) {
    segments.push(
      `${excludedCount.value.toLocaleString()} ${t('ARCHIVES.EXCLUDED_FROM_KPI_SUFFIX')}`
    );
  }
  return segments.length > 1
    ? `${segments[0]} (${segments.slice(1).join(' \u00b7 ')})`
    : base;
});
const showClearSearch = computed(() =>
  Boolean(searchInput.value.trim() || searchQuery.value)
);
const hasConversations = computed(() => conversations.value.length > 0);
const isMissedWindowMode = computed(() =>
  Boolean(route.query?.missed_event_id)
);

const activeMissedWindow = computed(() => {
  if (!isMissedWindowMode.value) return null;
  const eventId = String(route.query.missed_event_id);
  return missedWindows.value.find(w => String(w.event_id) === eventId) || null;
});

const showConversationSidebar = computed(() => {
  if (!currentChat.value?.id) return false;

  // Archives Missed/Excluded views are audit-only — sidebar (with the inline
  // assignee dropdown) is hidden so admins cannot accidentally reassign and
  // re-attribute KPI while reviewing past misses. The banner above
  // ConversationBox provides an "Open in Conversations" escape hatch for the
  // case where they really do need to manage the conversation.
  if (isMissedOrExcludedArchive.value) return false;

  const {
    is_contact_sidebar_open: isContactSidebarOpen,
    is_ai_assistant_open: isAiAssistantOpen,
  } = uiSettings.value || {};

  return Boolean(isContactSidebarOpen || isAiAssistantOpen);
});
const toLocaleDateStr = (date, options) =>
  new Intl.DateTimeFormat(locale.value || undefined, options).format(date);

const dateRangeLabel = computed(() => {
  if (!hasDateFilter.value) {
    return t('ARCHIVES.DATE_ALL');
  }
  const opts = { day: 'numeric', month: 'short', year: 'numeric' };
  return `${toLocaleDateStr(fromUnixTime(reportFrom.value), opts)} - ${toLocaleDateStr(fromUnixTime(reportTo.value), opts)}`;
});

const buildInitials = value => {
  const source = String(value || '').trim();
  if (!source) return '?';

  const words = source.split(/\s+/).filter(Boolean);
  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase();
  }

  return words
    .slice(0, 2)
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase();
};

const normalizeMessagePreview = content => {
  const preview = String(content || '')
    .replace(/\s+/g, ' ')
    .trim();
  return preview || t('ARCHIVES.NO_MESSAGE_CONTENT');
};

const RESOLVED_INACTIVITY_NOTICE_REGEX =
  /^Conversation was (marked )?resolved by .*due to inactivity/i;
const LEGACY_AUTO_RESOLVE_ACTIVITY_REGEX =
  /^\[system\]\s*Auto-resolved due to inactivity/i;

const isIgnoredPreviewMessage = message => {
  if (!message) return true;

  const messageType = Number(message.message_type);
  if (messageType === 2) {
    return true;
  }

  const normalizedContent = String(message.content || '').trim();
  if (!normalizedContent) return true;

  return (
    RESOLVED_INACTIVITY_NOTICE_REGEX.test(normalizedContent) ||
    LEGACY_AUTO_RESOLVE_ACTIVITY_REGEX.test(normalizedContent)
  );
};

const normalizePreviewMessages = messages => {
  const uniqueMessages = [];
  const seen = new Set();

  messages.forEach(message => {
    if (isIgnoredPreviewMessage(message)) return;

    const normalizedId = Number(message?.id || 0);
    const dedupeKey =
      normalizedId > 0
        ? `id:${normalizedId}`
        : `fallback:${message?.message_type}:${message?.created_at}:${message?.content}`;

    if (seen.has(dedupeKey)) return;
    seen.add(dedupeKey);
    uniqueMessages.push(message);
  });

  return uniqueMessages.slice(0, 3);
};

const missedSessionCount = conversation => {
  const id = String(conversation?.id || '');
  return Number(missedSessionsPerConversation.value[id] || 0);
};

const isConversationExcluded = conversation =>
  Boolean(overridesByConversationId.value[String(conversation?.id || '')]);

const overrideForConversation = conversation =>
  overridesByConversationId.value[String(conversation?.id || '')] || null;

// Two parallel lookup maps:
// - conversation-wide overrides (legacy & still used when no event_id is set)
// - per-event overrides (newer per-window exclude flow)
const overridesByEventId = ref({});

const fetchMissedChatsOverrides = async () => {
  if (
    (!isMissedChatsArchive.value && !isExcludedFromKpiArchive.value) ||
    !canExcludeFromMissedChats.value
  )
    return;
  try {
    const response = await ReportingOverridesAPI.get({
      scope: 'missed_chats',
      status: 'approved',
    });
    const overrides = response?.data || [];
    const convMap = {};
    const eventMap = {};
    overrides.forEach(override => {
      if (override.reporting_event_id) {
        eventMap[String(override.reporting_event_id)] = override;
        return;
      }
      const key = override.conversation_display_id ?? override.conversation_id;
      if (key === undefined || key === null) return;
      convMap[String(key)] = override;
    });
    overridesByConversationId.value = convMap;
    overridesByEventId.value = eventMap;
  } catch {
    // non-critical; silently ignore
  }
};

const toggleMissedChatsExclusion = async (conversation, window = null) => {
  const conversationId = Number(conversation?.id || 0);
  // Loading-key choice: per-event when targeting a single window so two
  // windows in the same conversation can be toggled independently.
  const loadingKey = window?.event_id
    ? `event:${window.event_id}`
    : `conv:${conversationId}`;
  if (!conversationId || overrideLoadingIds.value[loadingKey]) return;

  overrideLoadingIds.value = {
    ...overrideLoadingIds.value,
    [loadingKey]: true,
  };
  const existing = window?.event_id
    ? overridesByEventId.value[String(window.event_id)] || null
    : overrideForConversation(conversation);
  try {
    if (existing) {
      await ReportingOverridesAPI.revoke(existing.id);
      if (window?.event_id) {
        const next = { ...overridesByEventId.value };
        delete next[String(window.event_id)];
        overridesByEventId.value = next;
      } else {
        const next = { ...overridesByConversationId.value };
        delete next[String(conversationId)];
        overridesByConversationId.value = next;
      }
      useAlert(t('ARCHIVES.OVERRIDE_RESTORE_SUCCESS'));
      resetAndFetchConversations().catch(() => undefined);
    } else {
      const payload = {
        conversation_display_id: conversationId,
        scope: 'missed_chats',
        reason: 'Excluded by admin review',
      };
      if (window?.event_id) payload.reporting_event_id = window.event_id;

      const response = await ReportingOverridesAPI.create(payload);
      if (window?.event_id) {
        overridesByEventId.value = {
          ...overridesByEventId.value,
          [String(window.event_id)]: response?.data,
        };
      } else {
        overridesByConversationId.value = {
          ...overridesByConversationId.value,
          [String(conversationId)]: response?.data,
        };
      }
      useAlert(t('ARCHIVES.OVERRIDE_EXCLUDE_SUCCESS'));
      resetAndFetchConversations().catch(() => undefined);
    }
  } catch {
    useAlert(
      existing
        ? t('ARCHIVES.OVERRIDE_RESTORE_ERROR')
        : t('ARCHIVES.OVERRIDE_EXCLUDE_ERROR')
    );
  } finally {
    const next = { ...overrideLoadingIds.value };
    delete next[loadingKey];
    overrideLoadingIds.value = next;
  }
};

const conversationDisplayName = conversation => {
  return (
    conversation?.meta?.sender?.name ||
    conversation?.meta?.sender?.phone_number ||
    `#${conversation?.id}`
  );
};

const isMissedOrExcludedArchive = computed(
  () => isMissedChatsArchive.value || isExcludedFromKpiArchive.value
);

const missedSessionAssigneeFor = conversation => {
  const id = conversation?.id;
  if (!id) return null;
  return missedSessionAssignees.value[id] || null;
};

const conversationAssigneeName = conversation => {
  if (isMissedOrExcludedArchive.value) {
    const snapshot = missedSessionAssigneeFor(conversation);
    if (snapshot?.name) return snapshot.name;
  }
  return conversation?.meta?.assignee?.name || t('ARCHIVES.UNASSIGNED');
};

const conversationAssigneeIsSnapshot = conversation => {
  if (!isMissedOrExcludedArchive.value) return false;
  return Boolean(missedSessionAssigneeFor(conversation)?.snapshot);
};

// Per-window archive list — in missed/excluded modes, the list shows one row
// per missed window (a conversation may produce multiple rows). For other
// archive types, fall back to one row per conversation. Each item carries the
// conversation it belongs to so the existing row template keeps working.
//
// Lookup key is display_id because the conversations API exposes
// conversation.id == conversation.display_id (see _conversation.json.jbuilder),
// while the missed_windows payload carries both internal conversation_id and
// conversation_display_id. Matching by display_id keeps the two in sync.
const archiveItems = computed(() => {
  if (
    isMissedOrExcludedArchive.value &&
    Array.isArray(missedWindows.value) &&
    missedWindows.value.length > 0
  ) {
    const conversationsByDisplayId = new Map(
      conversations.value.map(c => [Number(c.id), c])
    );
    return missedWindows.value.flatMap(window => {
      const conversation =
        conversationsByDisplayId.get(Number(window.conversation_display_id)) ||
        null;
      if (!conversation) return [];
      return [
        {
          key: `window-${window.event_id}`,
          conversation,
          window,
        },
      ];
    });
  }

  return conversations.value.map(conversation => ({
    key: `conv-${conversation.id}`,
    conversation,
    window: null,
  }));
});

const formatWindowTime = unixSeconds => {
  if (!unixSeconds) return '';
  try {
    return new Intl.DateTimeFormat(undefined, {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(new Date(Number(unixSeconds) * 1000));
  } catch {
    return '';
  }
};

const formatWindowRange = window => {
  if (!window) return '';
  const start = formatWindowTime(window.window_start);
  const end = formatWindowTime(window.window_end);
  if (!start || !end) return start || end || '';
  return `${start} → ${end}`;
};

const windowAssigneeName = window => {
  if (!window?.assignee) return t('ARCHIVES.UNASSIGNED');
  return window.assignee.name || t('ARCHIVES.UNASSIGNED');
};

const isWindowExcluded = window => Boolean(window?.excluded);

const archiveItemKey = item =>
  `${item.key}-${
    item.window ? Number(item.window.window_start || 0) : 'static'
  }`;

const conversationDateSource = conversation => {
  const rawValue =
    conversation?.created_at ||
    conversation?.last_activity_at ||
    conversation?.timestamp;
  const ts = Number(rawValue);
  if (Number.isFinite(ts) && ts > 0) {
    return fromUnixTime(ts);
  }
  if (rawValue && typeof rawValue === 'string') {
    const parsed = parseISO(rawValue);
    if (!isNaN(parsed.getTime())) return parsed;
  }
  return new Date();
};

const conversationDateLabel = conversation => {
  return toLocaleDateStr(conversationDateSource(conversation), {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const conversationDateTitle = conversation => {
  return toLocaleDateStr(conversationDateSource(conversation), {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const conversationPreviewMessages = conversation => {
  const previewMessages = Array.isArray(conversation?.preview_messages)
    ? conversation.preview_messages
    : [];

  const normalizedPreviewMessages = normalizePreviewMessages(previewMessages);
  if (normalizedPreviewMessages.length) {
    return normalizedPreviewMessages;
  }

  const fallbackMessages = [
    conversation?.last_non_activity_message,
    ...(Array.isArray(conversation?.messages) ? conversation.messages : []),
  ].filter(Boolean);

  return normalizePreviewMessages(fallbackMessages);
};

const messageBadge = message => {
  return buildInitials(
    message?.sender?.available_name ||
      message?.sender?.name ||
      message?.sender?.phone_number
  );
};

const messagePreview = message => normalizeMessagePreview(message?.content);

const normalizePositiveId = value => {
  const normalizedId = Number(value || 0);
  return Number.isFinite(normalizedId) && normalizedId > 0
    ? normalizedId
    : null;
};

const parsePositiveIdList = value => {
  const source = Array.isArray(value) ? value : String(value || '').split(',');
  return source
    .map(item => normalizePositiveId(item))
    .filter(Boolean)
    .sort((left, right) => left - right);
};

const normalizeArchiveFilterState = ({
  selectedInbox = null,
  selectedAgents = [],
  selectedLabel = null,
  selectedTeam = null,
  selectedVip = null,
  selectedIssue = null,
  selectedPriority = null,
} = {}) => ({
  inboxId: normalizePositiveId(selectedInbox?.id),
  userIds: Array.isArray(selectedAgents)
    ? selectedAgents
        .map(agent => normalizePositiveId(agent?.id))
        .filter(Boolean)
        .sort((left, right) => left - right)
    : [],
  labelId: normalizePositiveId(selectedLabel?.id),
  teamId: normalizePositiveId(selectedTeam?.id),
  vipLevelId: normalizePositiveId(selectedVip?.id),
  issueId: normalizePositiveId(selectedIssue?.id),
  priority: selectedPriority?.id || null,
});

const buildArchiveFilterStateFromQuery = query => ({
  inboxId: normalizePositiveId(query?.inbox_id),
  inboxIds: parsePositiveIdList(query?.inbox_ids),
  userIds: parsePositiveIdList(query?.user_ids),
  labelId: normalizePositiveId(query?.label_id),
  teamId: normalizePositiveId(query?.team_id),
  vipLevelId: normalizePositiveId(query?.vip_level_id),
  issueId: normalizePositiveId(query?.issue_id),
  priority: String(query?.priority || '').trim() || null,
});

archiveFilters.value = buildArchiveFilterStateFromQuery(route.query);

const areArchiveFiltersEqual = (left, right) => {
  const leftInboxIds = left.inboxIds || [];
  const rightInboxIds = right.inboxIds || [];
  return (
    left.inboxId === right.inboxId &&
    leftInboxIds.length === rightInboxIds.length &&
    leftInboxIds.every((value, index) => value === rightInboxIds[index]) &&
    left.labelId === right.labelId &&
    left.teamId === right.teamId &&
    left.vipLevelId === right.vipLevelId &&
    left.issueId === right.issueId &&
    left.priority === right.priority &&
    left.userIds.length === right.userIds.length &&
    left.userIds.every((value, index) => value === right.userIds[index])
  );
};

async function resetAndFetchConversations() {
  if (!isViewActive.value) return;
  page.value = 1;
  hasMore.value = false;
  conversations.value = [];
  totalCount.value = 0;
  hasTotalCount.value = false;
  excludedCount.value = 0;
  // eslint-disable-next-line no-use-before-define
  await fetchArchiveConversations();
}

const clearScheduledArchiveRefresh = () => {
  if (!archiveFilterRefreshTimeoutId) return;
  window.clearTimeout(archiveFilterRefreshTimeoutId);
  archiveFilterRefreshTimeoutId = null;
};

const scheduleArchiveRefresh = () => {
  clearScheduledArchiveRefresh();
  archiveFilterRefreshTimeoutId = window.setTimeout(() => {
    archiveFilterRefreshTimeoutId = null;
    if (!isViewActive.value) return;
    resetAndFetchConversations().catch(() => undefined);
  }, 120);
};

const onArchiveFilterChange = async payload => {
  const nextFilters = normalizeArchiveFilterState(payload);
  if (areArchiveFiltersEqual(archiveFilters.value, nextFilters)) {
    return;
  }

  await replaceRouteQuery({
    inbox_id: nextFilters.inboxId || undefined,
    user_ids: nextFilters.userIds.length
      ? nextFilters.userIds.join(',')
      : undefined,
    label_id: nextFilters.labelId || undefined,
    team_id: nextFilters.teamId || undefined,
    vip_level_id: nextFilters.vipLevelId || undefined,
    issue_id: nextFilters.issueId || undefined,
    priority: nextFilters.priority || undefined,
  });
};

const buildRequestParams = pageNumber => {
  const inboxIds = archiveFilters.value.inboxIds || [];
  return {
    inboxId: archiveFilters.value.inboxId || undefined,
    inboxIds: inboxIds.length ? inboxIds : undefined,
    assigneeType: 'all',
    status: 'resolved',
    page: pageNumber,
    userIds: archiveFilters.value.userIds.length
      ? archiveFilters.value.userIds
      : undefined,
    labelId: archiveFilters.value.labelId || undefined,
    teamId: archiveFilters.value.teamId || undefined,
    vipLevelId: archiveFilters.value.vipLevelId || undefined,
    issueId: archiveFilters.value.issueId || undefined,
    priority: archiveFilters.value.priority || undefined,
    sortBy: sortOrder.value,
    q: searchQuery.value || undefined,
    conversationType: archiveConversationType.value,
    reportFrom: reportFrom.value || undefined,
    reportTo: reportTo.value || undefined,
    previewMessagesCount: 3,
    includeMeta: false,
  };
};

const mergeConversations = incomingConversations => {
  const merged = new Map(
    conversations.value.map(conversation => [conversation.id, conversation])
  );

  incomingConversations.forEach(conversation => {
    merged.set(conversation.id, conversation);
  });

  return Array.from(merged.values());
};

const clearSelectedState = async () => {
  await store.dispatch('clearSelectedState');
};

const ensureContactSidebarOpen = () => {
  if (!currentChat.value?.id) return;

  const {
    is_contact_sidebar_open: isContactSidebarOpen,
    is_ai_assistant_open: isAiAssistantOpen,
  } = uiSettings.value || {};

  if (isContactSidebarOpen || isAiAssistantOpen) return;

  updateUISettings({
    is_contact_sidebar_open: true,
    is_ai_assistant_open: false,
    is_copilot_panel_open: false,
  });
};

const findListedConversationById = conversationId => {
  const targetId = Number(conversationId || 0);
  if (!targetId) return null;

  return (
    conversations.value.find(
      conversation => Number(conversation?.id || 0) === targetId
    ) || null
  );
};

const loadConversation = conversationId => {
  const nextConversationId = Number(conversationId || 0);
  if (!nextConversationId || !isViewActive.value) return;
  if (Number(currentChat.value?.id || 0) === nextConversationId) return;

  // ── Synchronous lookup ──────────────────────────────────────────────────
  // Mirrors ConversationView.setActiveChat(): find the conversation from the
  // Vuex store (pre-populated by fetchArchiveConversations), then dispatch
  // setActiveChat as fire-and-forget.  The first synchronous commit inside
  // setActiveChat (SET_CURRENT_CHAT_WINDOW) switches the UI instantly;
  // message fetching happens silently in the background – identical to the
  // normal conversation page.
  let conversation = conversationById.value(nextConversationId);

  if (!conversation) {
    const listed = findListedConversationById(nextConversationId);
    if (listed) {
      store.commit('UPDATE_CONVERSATION', listed);
      conversation = conversationById.value(nextConversationId) || listed;
    }
  }

  if (!conversation) {
    // Deep-linked URL to a conversation not in our list – fetch from API.
    store
      .dispatch('getConversation', nextConversationId)
      .then(() => {
        if (!isViewActive.value) return;
        const fetched = conversationById.value(nextConversationId);
        if (!fetched) return;
        store.dispatch('setActiveChat', { data: fetched }).then(() => {
          if (isViewActive.value) ensureContactSidebarOpen();
        });
      })
      .catch(() => undefined);
    return;
  }

  if (conversation.id === currentChat.value?.id) return;

  listError.value = '';
  store.dispatch('setActiveChat', { data: conversation }).then(() => {
    if (isViewActive.value) {
      ensureContactSidebarOpen();
      emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
    }
  });
};

const openInConversations = async () => {
  const conversationId = Number(currentChat.value?.id || 0);
  if (!conversationId || !route.params?.accountId) return;
  // Leaves Archives audit mode and lands on the regular conversation page,
  // where the sidebar with the assignee dropdown is available again and the
  // full transcript is visible. We use push (not replace) so admins can hit
  // Back to return to the Archive view they came from.
  await router.push({
    name: 'inbox_conversation',
    params: {
      accountId: route.params.accountId,
      conversation_id: conversationId,
    },
  });
};

const navigateToConversation = async (conversationId, options = {}) => {
  if (!isViewActive.value) return;
  const nextConversationId = Number(conversationId || 0);
  if (!nextConversationId) return;

  // When navigating into a specific missed window, drop query params so the
  // conversation view can filter to just that window's messages and hide the
  // sidebar (Part B per-window mode). Strip the params when not provided so
  // switching between windows or back to a non-missed view does not leak old
  // state. window_start / window_end are also baked into the URL so MessagesView
  // can filter without coupling back into this component's state.
  const nextQuery = { ...(route.query || {}) };
  if (options.missedEventId) {
    nextQuery.missed_event_id = String(options.missedEventId);
    if (options.windowStart != null) {
      nextQuery.missed_window_start = String(options.windowStart);
    } else {
      delete nextQuery.missed_window_start;
    }
    if (options.windowEnd != null) {
      nextQuery.missed_window_end = String(options.windowEnd);
    } else {
      delete nextQuery.missed_window_end;
    }
  } else {
    delete nextQuery.missed_event_id;
    delete nextQuery.missed_window_start;
    delete nextQuery.missed_window_end;
  }

  if (
    nextConversationId === selectedConversationId.value &&
    String(nextQuery.missed_event_id || '') ===
      String(route.query?.missed_event_id || '')
  ) {
    loadConversation(nextConversationId);
    return;
  }

  if (!isViewActive.value) return;
  await router.replace({
    name: 'archives_view',
    params: {
      accountId: route.params.accountId,
      conversationId: nextConversationId,
    },
    query: nextQuery,
  });
};

const syncSelectionWithRoute = async () => {
  if (!isViewActive.value) return;
  if (!conversations.value.length) {
    await clearSelectedState();
    if (!isViewActive.value) return;

    if (selectedConversationId.value) {
      if (!isViewActive.value) return;
      await router.replace({
        name: 'archives_view',
        params: { accountId: route.params.accountId },
        query: route.query,
      });
    }
    return;
  }

  const matchingConversation = conversations.value.find(
    conversation => Number(conversation.id) === selectedConversationId.value
  );

  if (!matchingConversation) {
    const fallbackConversationId = Number(conversations.value[0]?.id || 0);
    if (!fallbackConversationId) return;

    if (!isViewActive.value) return;
    await router.replace({
      name: 'archives_view',
      params: {
        accountId: route.params.accountId,
        conversationId: fallbackConversationId,
      },
      query: route.query,
    });
    loadConversation(fallbackConversationId);
    return;
  }

  loadConversation(matchingConversation.id);
};

const fetchArchiveConversations = async ({ append = false } = {}) => {
  if (!isViewActive.value) return;
  latestListRequestToken.value += 1;
  const requestToken = latestListRequestToken.value;

  if (append) {
    if (isFetchingMore.value || isLoadingList.value || !hasMore.value) return;
    isFetchingMore.value = true;
  } else {
    isLoadingList.value = true;
    listError.value = '';
    isFetchingMore.value = false;
  }

  const nextPage = append ? page.value + 1 : 1;

  try {
    const response = await ConversationApi.get(buildRequestParams(nextPage));
    if (!isViewActive.value || requestToken !== latestListRequestToken.value)
      return;

    const payload = response?.data?.data?.payload || [];
    const meta = response?.data?.data?.meta || {};

    conversations.value = append ? mergeConversations(payload) : payload;

    // Pre-push every fetched conversation into the Vuex store so that
    // loadConversation can find them synchronously (same approach the
    // normal ChatList / ConversationView uses).
    payload.forEach(c => {
      if (!conversationById.value(Number(c.id))) {
        store.commit('UPDATE_CONVERSATION', c);
      }
    });

    page.value = nextPage;
    if (meta.missed_sessions_per_conversation) {
      missedSessionsPerConversation.value =
        meta.missed_sessions_per_conversation;
      totalMissedSessions.value = Number(meta.total_missed_sessions || 0);
    }
    if (meta.missed_session_assignees) {
      missedSessionAssignees.value = meta.missed_session_assignees;
    } else if (meta.missed_sessions_per_conversation) {
      missedSessionAssignees.value = {};
    }
    // Per-window list — each entry is one missed window. Frontend renders 1 row
    // per entry rather than 1 row per conversation, so admins can drill into a
    // single missed window and exclude/restore it independently.
    if (Array.isArray(meta.missed_windows)) {
      missedWindows.value = append
        ? [...missedWindows.value, ...meta.missed_windows]
        : meta.missed_windows;
    } else if (!append) {
      missedWindows.value = [];
    }
    if (meta.excluded_count !== undefined) {
      excludedCount.value = Number(meta.excluded_count || 0);
    }
    const allCount = Number(meta.all_count || 0);
    const hasCountFromResponse = Number.isFinite(allCount) && allCount > 0;

    if (hasCountFromResponse) {
      totalCount.value = allCount;
      hasTotalCount.value = true;
    } else if (!hasTotalCount.value) {
      totalCount.value = conversations.value.length || 0;
    }

    hasMore.value = hasTotalCount.value
      ? conversations.value.length < totalCount.value && payload.length > 0
      : payload.length >= RESULTS_PER_PAGE;

    if (!append) {
      syncSelectionWithRoute().catch(() => undefined);
      fetchMissedChatsOverrides().catch(() => undefined);
    }
  } catch {
    if (!isViewActive.value || requestToken !== latestListRequestToken.value)
      return;
    if (!append) {
      conversations.value = [];
      totalCount.value = 0;
      hasTotalCount.value = false;
      excludedCount.value = 0;
      hasMore.value = false;
      listError.value = t('ARCHIVES.LOAD_ERROR');
      await clearSelectedState();
    }
  } finally {
    if (isViewActive.value && requestToken === latestListRequestToken.value) {
      isLoadingList.value = false;
      isFetchingMore.value = false;
    }
  }
};

const applySearch = async () => {
  clearScheduledArchiveRefresh();
  searchQuery.value = searchInput.value.trim();
  await resetAndFetchConversations();
};

const clearSearch = async () => {
  if (!showClearSearch.value) return;
  clearScheduledArchiveRefresh();
  searchInput.value = '';
  searchQuery.value = '';
  await resetAndFetchConversations();
};

const toggleSortOrder = async () => {
  const nextSort =
    sortOrder.value === 'last_activity_at_desc'
      ? 'last_activity_at_asc'
      : 'last_activity_at_desc';
  sortOrder.value = nextSort;
  await replaceRouteQuery({
    sort_by: nextSort === 'last_activity_at_desc' ? undefined : nextSort,
  });
};

const replaceRouteQuery = async queryPatch => {
  const nextQuery = {
    ...route.query,
    ...queryPatch,
  };

  Object.keys(nextQuery).forEach(key => {
    if (
      nextQuery[key] === undefined ||
      nextQuery[key] === null ||
      nextQuery[key] === ''
    ) {
      delete nextQuery[key];
    }
  });

  await router.replace({
    name: 'archives_view',
    params: selectedConversationId.value
      ? {
          accountId: route.params.accountId,
          conversationId: selectedConversationId.value,
        }
      : { accountId: route.params.accountId },
    query: nextQuery,
  });
};

const clearDateFilter = async () => {
  await replaceRouteQuery({ from: undefined, to: undefined });
};

const setArchiveFilter = async value => {
  await replaceRouteQuery({
    archive_filter:
      value === 'missed_chats' ||
      value === 'excluded_from_kpi' ||
      value === 'csat_rated_good' ||
      value === 'csat_rated_bad' ||
      value === 'vip_chats'
        ? value
        : undefined,
  });
  showFilterMenu.value = false;
};

const clearArchiveFilter = async () => {
  await replaceRouteQuery({ archive_filter: undefined });
};

const handleListScroll = async event => {
  const element = event?.target;
  if (!element || !hasMore.value || isFetchingMore.value) return;

  const distanceToBottom =
    element.scrollHeight - element.scrollTop - element.clientHeight;

  if (distanceToBottom < 120) {
    await fetchArchiveConversations({ append: true });
  }
};

watch(
  () => [
    route.query.from,
    route.query.to,
    route.query.archive_filter,
    route.query.inbox_id,
    route.query.inbox_ids,
    route.query.user_ids,
    route.query.label_id,
    route.query.team_id,
    route.query.vip_level_id,
    route.query.issue_id,
    route.query.priority,
    route.query.sort_by,
  ],
  async () => {
    if (!isViewActive.value) return;
    archiveFilters.value = buildArchiveFilterStateFromQuery(route.query);
    sortOrder.value = String(route.query.sort_by || 'last_activity_at_desc');
    clearScheduledArchiveRefresh();
    await resetAndFetchConversations();
  }
);

watch(selectedConversationId, async conversationId => {
  if (!isViewActive.value) return;
  if (!conversations.value.length) return;

  if (!conversationId) {
    await syncSelectionWithRoute();
    return;
  }

  loadConversation(conversationId);
});

// Use MISSED_CHATS_INBOX_HINT when available; fall back to a safe EN string
// so users never see a raw i18n key if the locale bundle is outdated.
const missedChatsInboxHint = computed(() => {
  const translated = t('ARCHIVES.MISSED_CHATS_INBOX_HINT');
  if (translated && translated !== 'ARCHIVES.MISSED_CHATS_INBOX_HINT') {
    return translated;
  }
  return 'Use the Inbox filter to narrow results by team or agent.';
});

const goBackToArchiveSource = async () => {
  if (isMissedChatsArchive.value || isExcludedFromKpiArchive.value) {
    await router.push({
      name: 'missed_chats_reports',
      params: { accountId: route.params.accountId },
    });
    return;
  }

  if (isCsatArchive.value) {
    await router.push({
      name: 'csat_reports',
      params: { accountId: route.params.accountId },
    });
  }
};

onMounted(async () => {
  store.commit('CLEAR_LIST_LOADING_STATUS');
  searchInput.value = String(route.query.q || '');
  searchQuery.value = String(route.query.q || '');
  sortOrder.value = String(route.query.sort_by || 'last_activity_at_desc');
  await resetAndFetchConversations();
});

const markViewInactive = () => {
  isViewActive.value = false;
  clearScheduledArchiveRefresh();
  latestListRequestToken.value += 1;
};

onBeforeUnmount(() => {
  markViewInactive();
  clearSelectedState().catch(() => undefined);
});
</script>

<template>
  <section class="archives-layout conversation-page-theme">
    <div class="css-og8kck archives-sidebar-shell">
      <div class="css-sn4l67">
        <div class="css-sn4l67">
          <div class="css-1py3ffp archives-sidebar-panel">
            <div class="css-116lk7b">
              <div class="css-e76p5t">
                <div class="css-111rffh">{{ t('ARCHIVES.TITLE') }}</div>
                <div class="css-kr8fjv">
                  <div class="css-mc000a archives-filter-anchor">
                    <button
                      class="archives-icon-button"
                      type="button"
                      @click="showFilterMenu = !showFilterMenu"
                    >
                      <span class="archives-icon-wrap">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          width="20"
                          height="20"
                        >
                          <path
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M10 12h10m-10 0a2 2 0 0 1-4 0m4 0a2 2 0 0 0-4 0m0 0H4m11 6a2 2 0 0 0 4 0m-4 0a2 2 0 0 1 4 0m-4 0H4m15 0h1M12 6a2 2 0 1 0 4 0m-4 0a2 2 0 1 1 4 0m-4 0H4m12 0h4"
                          />
                        </svg>
                      </span>
                    </button>

                    <div
                      v-if="showFilterMenu"
                      v-on-click-outside="() => (showFilterMenu = false)"
                      class="archives-filter-menu"
                    >
                      <button
                        class="archives-filter-menu__item"
                        :class="{ 'is-selected': isMissedChatsArchive }"
                        type="button"
                        @click="setArchiveFilter('missed_chats')"
                      >
                        {{ t('ARCHIVES.FILTER_MISSED_CHATS') }}
                      </button>
                      <button
                        class="archives-filter-menu__item"
                        :class="{ 'is-selected': isExcludedFromKpiArchive }"
                        type="button"
                        @click="setArchiveFilter('excluded_from_kpi')"
                      >
                        {{ t('ARCHIVES.FILTER_EXCLUDED_FROM_KPI') }}
                      </button>
                      <button
                        class="archives-filter-menu__item"
                        :class="{ 'is-selected': isCsatRatedGoodArchive }"
                        type="button"
                        @click="setArchiveFilter('csat_rated_good')"
                      >
                        {{ t('ARCHIVES.FILTER_CSAT_GOOD') }}
                      </button>
                      <button
                        class="archives-filter-menu__item"
                        :class="{ 'is-selected': isCsatRatedBadArchive }"
                        type="button"
                        @click="setArchiveFilter('csat_rated_bad')"
                      >
                        {{ t('ARCHIVES.FILTER_CSAT_BAD') }}
                      </button>
                      <button
                        class="archives-filter-menu__item"
                        :class="{ 'is-selected': isVipChatsArchive }"
                        type="button"
                        @click="setArchiveFilter('vip_chats')"
                      >
                        {{ t('ARCHIVES.FILTER_VIP') }}
                      </button>
                      <button
                        class="archives-filter-menu__item"
                        :class="{ 'is-selected': showAllArchivedChats }"
                        type="button"
                        @click="setArchiveFilter(undefined)"
                      >
                        {{ t('ARCHIVES.FILTER_ALL') }}
                      </button>
                      <button
                        v-if="hasDateFilter"
                        class="archives-filter-menu__item"
                        type="button"
                        @click="clearDateFilter"
                      >
                        {{ t('ARCHIVES.FILTER_CLEAR_DATE') }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="
                isMissedChatsArchive ||
                isExcludedFromKpiArchive ||
                isCsatArchive
              "
              class="archives-back-row"
            >
              <button
                class="archives-back-button"
                type="button"
                @click="goBackToArchiveSource"
              >
                <span class="archives-back-button__icon" aria-hidden="true">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                  >
                    <path
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="m15 19-7-7 7-7"
                    />
                  </svg>
                </span>
                <span>{{ archiveBackLabel }}</span>
              </button>
            </div>

            <div class="css-1j8yl8o">
              <div class="css-1b0jxoo">
                <div class="css-eox8i6">
                  <div class="css-a3l5mu">
                    <input
                      v-model="searchInput"
                      class="css-sv5c8s"
                      :placeholder="t('ARCHIVES.SEARCH_PLACEHOLDER')"
                      @keyup.enter="applySearch"
                    />
                    <div class="css-oayotw">
                      <div class="css-1hw56h8" />
                    </div>
                    <button
                      class="css-1jkeysg"
                      :disabled="!searchInput.trim()"
                      title="Search"
                      @click="applySearch"
                    >
                      <span class="archives-search-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          width="16"
                          height="16"
                        >
                          <path
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M3 10a7 7 0 1 0 14 0 7 7 0 0 0-14 0M21 21l-6-6"
                          />
                        </svg>
                      </span>
                    </button>
                    <button
                      class="css-z9j85r"
                      :class="{ 'is-visible': showClearSearch }"
                      title="Clear"
                      @click="clearSearch"
                    >
                      <span class="archives-clear-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          width="16"
                          height="16"
                        >
                          <path
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="m6 6 12 12m0-12L6 18"
                          />
                        </svg>
                      </span>
                    </button>
                  </div>

                  <div class="css-h23tv4">
                    <CompactDateInboxFilters
                      class="archives-add-filter-toolbar"
                      :show-date-filter="false"
                      :emit-initial-change="false"
                      :forced-inbox="
                        archiveFilters.inboxId
                          ? { id: archiveFilters.inboxId }
                          : null
                      "
                      :forced-agents="
                        archiveFilters.userIds.map(id => ({ id }))
                      "
                      :forced-label="
                        archiveFilters.labelId
                          ? { id: archiveFilters.labelId }
                          : null
                      "
                      :forced-team="
                        archiveFilters.teamId
                          ? { id: archiveFilters.teamId }
                          : null
                      "
                      :forced-vip="
                        archiveFilters.vipLevelId
                          ? { id: archiveFilters.vipLevelId }
                          : null
                      "
                      :forced-issue="
                        archiveFilters.issueId
                          ? { id: archiveFilters.issueId }
                          : null
                      "
                      :forced-priority="
                        archiveFilters.priority
                          ? { id: archiveFilters.priority }
                          : null
                      "
                      :show-agents="
                        !isMissedChatsArchive && !isExcludedFromKpiArchive
                      "
                      show-inbox
                      show-labels
                      show-teams
                      show-vip
                      show-issue
                      show-priority
                      hide-inactive-filters
                      @filter-change="onArchiveFilterChange"
                    />
                    <div
                      v-if="isMissedChatsArchive || isExcludedFromKpiArchive"
                      class="archives-missed-filter-note"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        width="14"
                        height="14"
                      >
                        <path
                          stroke="currentColor"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M12 8h.01M11 12h1v4h1M3 12a9 9 0 1 0 18 0 9 9 0 0 0-18 0"
                        />
                      </svg>
                      <span>{{ missedChatsInboxHint }}</span>
                    </div>

                    <div v-if="hasDateFilter" class="archives-chip">
                      <div class="archives-chip__content">
                        <span class="archives-chip__icon">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            width="20"
                            height="20"
                          >
                            <path
                              stroke="currentColor"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M4 11h16M8 3v4m8-4v4m-8 8h2v2H8zM4 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z"
                            />
                          </svg>
                        </span>
                        <div class="css-1rdflu1">
                          <span>
                            <strong>{{ t('ARCHIVES.DATE_LABEL') }}</strong>
                            <span>
                              <strong> {{ t('ARCHIVES.DATE_IS') }} </strong
                              >{{ dateRangeLabel }}
                            </span>
                          </span>
                        </div>
                      </div>
                      <button
                        class="archives-chip__remove"
                        type="button"
                        title="Remove"
                        @click="clearDateFilter"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          width="20"
                          height="20"
                        >
                          <path
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="m6 6 12 12m0-12L6 18"
                          />
                        </svg>
                      </button>
                    </div>

                    <div
                      v-if="isMissedChatsArchive || isExcludedFromKpiArchive"
                      class="archives-chip"
                    >
                      <div class="archives-chip__content">
                        <span class="archives-chip__icon">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            width="20"
                            height="20"
                          >
                            <path
                              stroke="currentColor"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="m21 6.99-7 9.5c-.077.115-.304.404-.432.457a.7.7 0 0 1-.763-.152L3 6.99m0 0h5m-5 0v5"
                            />
                          </svg>
                        </span>
                        <div class="css-o2hzyu">
                          {{
                            isExcludedFromKpiArchive
                              ? t('ARCHIVES.FILTER_EXCLUDED_FROM_KPI')
                              : t('MISSED_CHATS_REPORT.TITLE')
                          }}
                        </div>
                      </div>
                      <button
                        class="archives-chip__remove"
                        type="button"
                        title="Remove"
                        @click="clearArchiveFilter"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          width="20"
                          height="20"
                        >
                          <path
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="m6 6 12 12m0-12L6 18"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div class="css-1mwn02k">
                    <div class="css-ov1ktg">
                      <div>
                        <span>{{ countLabel }}</span>
                      </div>
                    </div>
                    <div class="css-1u525pn">
                      <button
                        class="archives-sort-button"
                        type="button"
                        @click="toggleSortOrder"
                      >
                        <span>{{ sortLabel }}</span>
                        <span class="archives-sort-button__icon">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            width="20"
                            height="20"
                          >
                            <path
                              stroke="currentColor"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="m6 9 6 6 6-6"
                            />
                          </svg>
                        </span>
                      </button>
                    </div>
                  </div>

                  <div v-if="listError" class="archives-inline-error">
                    {{ listError }}
                  </div>
                </div>

                <ul class="css-1yt0wlr" @scroll.passive="handleListScroll">
                  <li
                    v-for="item in archiveItems"
                    :id="`archive-item-${archiveItemKey(item)}`"
                    :key="archiveItemKey(item)"
                    class="privacy-masker"
                    :class="
                      Number(item.conversation.id) === selectedConversationId &&
                      String(item.window?.event_id || '') ===
                        String(route.query?.missed_event_id || '')
                        ? 'css-162royd'
                        : 'css-1wkoiq1'
                    "
                  >
                    <div
                      class="css-1i13yu"
                      @click="
                        navigateToConversation(item.conversation.id, {
                          missedEventId: item.window?.event_id || null,
                          windowStart: item.window?.window_start ?? null,
                          windowEnd: item.window?.window_end ?? null,
                        })
                      "
                    >
                      <div class="css-1ibr01v">
                        <div class="css-1iy6hkk">
                          <div class="css-1sg2lsz">
                            <p class="css-1yz5qyy archives-item-title">
                              {{ conversationDisplayName(item.conversation) }}
                            </p>
                          </div>
                          <p class="css-7mbya9">
                            <span>{{ t('ARCHIVES.AGENT_LABEL') }} </span>
                            <template v-if="item.window">
                              <span>{{ windowAssigneeName(item.window) }}</span>
                              <span
                                v-if="item.window.assignee"
                                class="archives-agent-snapshot-badge"
                                :title="t('ARCHIVES.AGENT_SNAPSHOT_TOOLTIP')"
                              >
                                {{ t('ARCHIVES.AGENT_SNAPSHOT_TAG') }}
                              </span>
                            </template>
                            <template v-else>
                              <span>{{
                                conversationAssigneeName(item.conversation)
                              }}</span>
                              <span
                                v-if="
                                  conversationAssigneeIsSnapshot(
                                    item.conversation
                                  )
                                "
                                class="archives-agent-snapshot-badge"
                                :title="t('ARCHIVES.AGENT_SNAPSHOT_TOOLTIP')"
                              >
                                {{ t('ARCHIVES.AGENT_SNAPSHOT_TAG') }}
                              </span>
                            </template>
                          </p>
                          <p
                            v-if="
                              !item.window &&
                              missedSessionCount(item.conversation) > 0
                            "
                            class="missed-sessions-badge"
                          >
                            {{
                              missedSessionCount(item.conversation) === 1
                                ? t('ARCHIVES.MISSED_ONCE', {
                                    count: missedSessionCount(
                                      item.conversation
                                    ),
                                  })
                                : t('ARCHIVES.MISSED_MANY', {
                                    count: missedSessionCount(
                                      item.conversation
                                    ),
                                  })
                            }}
                          </p>
                          <div
                            v-if="canExcludeFromMissedChats"
                            class="missed-chats-override-row"
                          >
                            <span
                              v-if="
                                item.window
                                  ? isWindowExcluded(item.window)
                                  : isConversationExcluded(item.conversation)
                              "
                              class="mc-excluded-badge"
                            >
                              {{ t('ARCHIVES.OVERRIDE_EXCLUDED_BADGE') }}
                            </span>
                            <button
                              type="button"
                              class="mc-override-btn"
                              :class="
                                (
                                  item.window
                                    ? isWindowExcluded(item.window)
                                    : isConversationExcluded(item.conversation)
                                )
                                  ? 'mc-override-btn--restore'
                                  : 'mc-override-btn--exclude'
                              "
                              :disabled="
                                !!overrideLoadingIds[
                                  item.window
                                    ? `event:${item.window.event_id}`
                                    : `conv:${item.conversation.id}`
                                ]
                              "
                              @click.stop="
                                toggleMissedChatsExclusion(
                                  item.conversation,
                                  item.window
                                )
                              "
                            >
                              <span
                                v-if="
                                  overrideLoadingIds[
                                    item.window
                                      ? `event:${item.window.event_id}`
                                      : `conv:${item.conversation.id}`
                                  ]
                                "
                              >
                                {{ t('ARCHIVES.OVERRIDE_LOADING') }}
                              </span>
                              <span
                                v-else-if="
                                  item.window
                                    ? isWindowExcluded(item.window)
                                    : isConversationExcluded(item.conversation)
                                "
                              >
                                {{ t('ARCHIVES.OVERRIDE_RESTORE_BTN') }}
                              </span>
                              <span v-else>{{
                                t('ARCHIVES.OVERRIDE_EXCLUDE_BTN')
                              }}</span>
                            </button>
                          </div>
                        </div>
                        <div
                          class="css-n3y541"
                          :title="conversationDateTitle(item.conversation)"
                        >
                          <span>{{
                            conversationDateLabel(item.conversation)
                          }}</span>
                        </div>
                      </div>

                      <div class="css-1sg2lsz">
                        <div class="css-nzt1xy">
                          <div
                            v-for="message in conversationPreviewMessages(
                              item.conversation
                            )"
                            :key="`${item.conversation.id}-${message.id}`"
                            class="css-wh4h37"
                          >
                            <span class="css-1ed8kz2">
                              {{ messageBadge(message) }}:
                            </span>
                            <span>
                              <span>{{ messagePreview(message) }}</span>
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>

                  <li
                    v-if="!isLoadingList && !hasConversations"
                    class="archives-empty-list"
                  >
                    {{ t('ARCHIVES.EMPTY_STATE') }}
                  </li>

                  <li
                    v-if="isLoadingList || isFetchingMore"
                    class="archives-loading-row"
                  >
                    <Spinner class="text-n-slate-11" />
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="css-v8ql05 archives-main-panel">
      <div
        v-if="isMissedOrExcludedArchive && currentChat.id"
        class="archives-missed-window-banner"
      >
        <div class="archives-missed-window-banner__info">
          <strong v-if="isMissedWindowMode && activeMissedWindow">
            {{ t('ARCHIVES.MISSED_WINDOW_BANNER_TITLE') }}
          </strong>
          <strong v-else>
            {{ t('ARCHIVES.AUDIT_MODE_BANNER_TITLE') }}
          </strong>
          <span
            v-if="isMissedWindowMode && activeMissedWindow"
            class="archives-missed-window-banner__range"
          >
            {{ formatWindowRange(activeMissedWindow) }}
          </span>
          <span v-else class="archives-missed-window-banner__range">
            {{ t('ARCHIVES.AUDIT_MODE_BANNER_NOTE') }}
          </span>
        </div>
        <div class="archives-missed-window-banner__actions">
          <button
            type="button"
            class="archives-missed-window-banner__open"
            @click="openInConversations"
          >
            {{ t('ARCHIVES.VIEW_FULL_CONVERSATION') }}
          </button>
        </div>
      </div>
      <ConversationBox
        class="flex-1 [&.conversation-details-wrap]:!border-0"
        :inbox-id="0"
        :is-on-expanded-layout="false"
        hide-reply-box
      >
        <SidepanelSwitch v-if="currentChat.id" />
      </ConversationBox>
    </div>

    <ConversationSidebar
      v-if="showConversationSidebar"
      :current-chat="currentChat"
    />
  </section>
</template>

<style scoped>
.missed-sessions-badge {
  display: inline-block;
  margin-top: 2px;
  padding: 1px 6px;
  font-size: 11px;
  font-weight: 600;
  color: rgb(var(--ruby-12));
  background-color: rgb(var(--ruby-1));
  border: 1px solid rgb(var(--ruby-9) / 0.35);
  border-radius: 4px;
  line-height: 1.4;
}

.archives-agent-snapshot-badge {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 5px;
  font-size: 10px;
  font-weight: 600;
  color: rgb(var(--slate-11));
  background-color: rgb(var(--slate-2));
  border: 1px solid rgb(var(--slate-6));
  border-radius: 3px;
  line-height: 1.4;
  cursor: help;
}

.archives-missed-window-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 16px;
  font-size: 12px;
  color: rgb(var(--slate-12));
  background-color: rgb(var(--ruby-1));
  border-bottom: 1px solid rgb(var(--ruby-9) / 0.35);
  /* Pin the banner to the top of the column-flex parent (archives-main-panel)
     so it does not get squeezed when ConversationBox grows below. */
  flex: 0 0 auto;
  width: 100%;
}
.archives-missed-window-banner__info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.archives-missed-window-banner__range {
  color: rgb(var(--slate-11));
  font-size: 11px;
}
.archives-missed-window-banner__actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.archives-missed-window-banner__open {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  /* Hardcoded white-on-blue so the button stays readable in light, dark, and
     mixed themes regardless of which CSS-variable scheme the page injects.
     The banner background can change with theme but the button needs a
     fixed contrast against any of them. */
  color: #ffffff;
  background-color: #1f93ff;
  border: 1px solid #1f93ff;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.15s ease;
}
.archives-missed-window-banner__open:hover {
  background-color: #1378d8;
  border-color: #1378d8;
}

.archives-layout {
  display: flex;
  width: 100%;
  height: 100%;
  min-width: 0;
  background-color: var(--wlab-bg-sidebar);
}

.archives-back-row {
  padding: 12px 12px 0 12px;
}

.archives-back-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid var(--border-basic-tertiary, #e5e7eb);
  background: var(--surface-primary-default, #ffffff);
  color: var(--content-basic-primary, #111827);
  height: 34px;
  padding: 0 10px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.archives-back-button:hover {
  background: var(--surface-secondary-hover, #f9fafb);
}

.archives-back-button__icon {
  display: inline-flex;
  align-items: center;
  color: currentColor;
}

.archives-sidebar-shell {
  background: var(--surface-primary-default, #ffffff);
  overflow: hidden;
}

.archives-sidebar-panel {
  display: flex;
  flex-direction: column;
}

.archives-main-panel {
  box-sizing: border-box;
  height: 100%;
  min-width: 320px;
  flex: 2 1 50%;
  border-left: 1px solid var(--border-basic-tertiary, #eeeeef);
  display: flex;
  /* Column layout so the missed-window banner can sit as a full-width strip
     above ConversationBox instead of being squeezed into a row alongside it. */
  flex-direction: column;
  overflow: hidden;
}

.archives-filter-anchor {
  position: relative;
}

.archives-filter-menu {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  min-width: 200px;
  border: 1px solid var(--border-basic-tertiary, #e5e7eb);
  border-radius: 10px;
  background: var(--surface-primary-default, #ffffff);
  box-shadow: rgba(0, 0, 0, 0.12) 0 14px 38px;
  padding: 6px;
  z-index: 40;
}

.archives-filter-menu__item {
  width: 100%;
  text-align: left;
  padding: 8px 10px;
  border: none;
  background: transparent;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--content-basic-primary, #111827);
  cursor: pointer;
}

.archives-filter-menu__item:hover {
  background: var(--surface-secondary-hover, #f3f4f6);
}

.archives-filter-menu__item.is-selected {
  background: #eef2ff;
  color: #1d4ed8;
}

.archives-icon-button {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 36px;
  padding: 4px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  color: var(--content-basic-primary, #111827);
  cursor: pointer;
}

.archives-icon-button:hover,
.archives-sort-button:hover {
  background: var(--surface-secondary-hover, #f3f4f6);
}

.archives-icon-wrap,
.archives-search-icon,
.archives-clear-icon,
.archives-secondary-button__icon,
.archives-sort-button__icon {
  display: flex;
  align-items: center;
}

.archives-secondary-button {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: 32px;
  height: 32px;
  padding: 6px 16px 6px 12px;
  border: 1px solid var(--border-basic-primary, #d1d5db);
  border-radius: 8px;
  background: var(--surface-primary-default, #ffffff);
  color: var(--content-basic-primary, #111827);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.archives-secondary-button:hover {
  background: var(--surface-secondary-hover, #f9fafb);
}

.archives-add-filter-toolbar {
  flex: 1 1 auto;
  min-width: 0;
  padding: 0;
}

.archives-add-filter-toolbar:deep(.cdf-row) {
  justify-content: flex-start;
}

.archives-add-filter-toolbar:deep(.cdf-left) {
  gap: 4px;
}

.archives-add-filter-toolbar:deep(.cdf-secondary-btn),
.archives-add-filter-toolbar:deep(.cdf-chip) {
  height: 32px;
}

.archives-chip {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  border-radius: 8px;
  border: 1px solid var(--border-basic-primary, #d1d5db);
  background-color: var(--surface-tertiary-default, #e6e6e7);
  color: var(--content-basic-primary, #111827);
  overflow: hidden;
  min-height: 32px;
  max-width: 100%;
}

.archives-chip__content {
  display: inline-flex;
  align-items: center;
  overflow: hidden;
  padding: 0 8px;
}

.archives-chip__icon {
  display: flex;
  align-items: center;
}

.archives-chip__remove {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 4px;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--content-basic-secondary, #6b7280);
  cursor: pointer;
}

.archives-sort-button {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: 32px;
  height: 32px;
  padding: 6px 12px 6px 16px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  color: var(--content-basic-primary, #111827);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.archives-inline-error {
  margin-top: 8px;
  border: 1px solid rgb(var(--ruby-9) / 0.35);
  background: rgb(var(--ruby-1));
  color: rgb(var(--ruby-12));
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
}

.archives-missed-filter-note {
  display: flex;
  align-items: flex-start;
  gap: 5px;
  margin-top: 6px;
  font-size: 11px;
  color: var(--content-basic-secondary, #6b7280);
  line-height: 1.4;
}

.archives-item-title {
  margin: 0;
  line-height: 21px;
  letter-spacing: 0;
  font-size: 14px;
  font-weight: 600;
  font-style: normal;
}

.archives-empty-list,
.archives-loading-row {
  padding: 20px 16px;
  text-align: center;
  color: #6b7280;
  font-size: 13px;
}

.archives-loading-row {
  display: flex;
  justify-content: center;
}

.css-og8kck {
  box-sizing: border-box;
  height: 100%;
  border-left: none;
  min-width: 320px;
  flex: 1 1 25%;
  transition: margin-left var(--transition-duration-fast-2, 0.2s)
    var(--transition-timing-ease-out, ease-out);
  will-change: margin-left;
  margin-left: 0;
}

.css-v8ql05 {
  box-sizing: border-box;
  height: 100%;
  min-width: 320px;
  flex: 2 1 50%;
  transition: margin-left var(--transition-duration-fast-2, 0.2s)
    var(--transition-timing-ease-out, ease-out);
  will-change: margin-left;
  margin-left: 0;
}

.css-sn4l67 {
  width: 100%;
  height: 100%;
  display: block;
}

.css-1py3ffp {
  position: relative;
  box-sizing: border-box;
  height: 100%;
  background-color: var(--surface-primary-default, #ffffff);
  color: var(--content-basic-primary, #111827);
  border-color: var(--border-basic-tertiary, #eeeeef);
}

.css-116lk7b {
  display: contents;
  height: 56px;
}

.css-e76p5t {
  display: flex;
  justify-content: space-between;
  width: 100%;
  height: 56px;
  position: relative;
  text-align: center;
  box-sizing: border-box;
  border-bottom: 1px solid var(--border-basic-tertiary, #eeeeef);
  background-color: var(--surface-primary-default, #ffffff);
}

.css-111rffh {
  color: var(--content-basic-primary, #111827);
  height: 56px;
  line-height: 56px;
  white-space: nowrap;
  box-sizing: border-box;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-right: 40px;
  margin-left: 0;
  width: calc(100% - 40px);
  text-align: left;
  padding-left: 16px;
  letter-spacing: 0;
  font-size: 18px;
  font-weight: 600;
  font-style: normal;
}

.css-kr8fjv {
  flex-shrink: 0;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.css-mc000a {
  padding: 8px 16px 8px 8px;
}

.css-1j8yl8o {
  height: calc(100% - 53px);
  overflow: hidden;
  z-index: 0;
  position: static;
}

.css-1b0jxoo {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: var(--surface-primary-default, #ffffff);
}

.css-eox8i6 {
  padding: 10px 12px;
  box-sizing: border-box;
  font-size: 12px;
  line-height: 15px;
  color: rgba(66, 77, 87, 0.8);
  width: 100%;
  box-shadow: rgba(0, 0, 0, 0.3) 0 3px 10px -6px;
}

.css-a3l5mu {
  position: relative;
  text-align: right;
  margin-bottom: 10px;
}

.css-sv5c8s {
  display: inline-block;
  width: 100%;
  height: 40px;
  padding: 0 36px;
  font-size: 13px;
  color: rgb(66, 77, 87);
  text-align: left;
  border-radius: 4px;
  border: 1px solid rgb(221, 226, 230);
  background-color: rgb(255, 255, 255);
  box-sizing: border-box;
  transition: border 0.2s ease-in-out;
}

.css-oayotw {
  width: 15px;
  height: 15px;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 13px;
}

.css-1hw56h8 {
  width: 100%;
  height: 100%;
  position: relative;
  visibility: hidden;
  opacity: 0;
  z-index: -1;
  border-radius: 50%;
  border-width: 2px;
  border-color: rgb(74, 144, 226) rgba(74, 144, 226, 0.5)
    rgba(74, 144, 226, 0.5);
  transition: 0.2s ease-in-out;
}

.css-1jkeysg,
.css-z9j85r {
  position: absolute;
  top: 0;
  height: 40px;
  padding: 0 13px;
  border: none;
  background: none;
  cursor: pointer;
  border-radius: 4px;
}

.css-1jkeysg {
  left: 0;
}

.css-z9j85r {
  right: 0;
  visibility: hidden;
  opacity: 0;
  z-index: -1;
  transition: 0.2s ease-in-out;
}

.css-z9j85r.is-visible {
  visibility: visible;
  opacity: 1;
  z-index: 1;
}

.css-h23tv4 {
  width: 100%;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  flex: 1 1 auto;
  box-sizing: border-box;
  gap: 4px;
}

.css-1rdflu1,
.css-o2hzyu {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.css-1rdflu1 {
  max-width: 190px;
}

.css-o2hzyu {
  max-width: 270px;
}

.css-1mwn02k {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
}

.css-ov1ktg {
  display: flex;
}

.css-1u525pn {
  margin-left: auto;
  display: flex;
}

.css-1yt0wlr {
  list-style: none;
  overflow-y: auto;
  flex: 1 1 0%;
  box-sizing: border-box;
  display: block;
  position: relative;
  margin: 0;
  padding: 0;
}

.css-162royd,
.css-1wkoiq1 {
  width: 100%;
  padding: 0 12px;
  box-sizing: border-box;
  position: relative;
}

.css-162royd {
  background-color: var(--surface-primary-active, #eeeeef);
}

.css-162royd::before,
.css-1wkoiq1::before {
  background-color: rgb(67, 132, 245);
  width: 5px;
  height: 100%;
  top: 0;
  left: 0;
  position: absolute;
  content: '';
  opacity: 0;
  visibility: hidden;
}

.css-162royd::before {
  opacity: 1;
  visibility: visible;
}

.css-1i13yu {
  padding: 10px 6px;
  width: 100%;
  box-sizing: border-box;
  border-bottom: 1px solid rgb(221, 226, 230);
  font-size: 14px;
  cursor: pointer;
  color: rgb(66, 77, 87);
}

.css-1ibr01v {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  align-items: flex-start;
}

.css-1iy6hkk {
  line-height: 20px;
  flex: 1 1 0%;
  overflow: hidden;
}

.css-1iy6hkk > * {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.css-1sg2lsz {
  display: flex;
  align-items: center;
}

.css-1yz5qyy {
  flex: 1 1 0%;
  text-overflow: ellipsis;
  overflow: hidden;
}

.css-7mbya9 {
  font-size: 13px;
  height: 20px;
  color: rgba(66, 77, 87, 0.6);
  margin: 0;
}

.css-n3y541 {
  color: rgba(66, 77, 87, 0.6);
  font-size: 12px;
  margin-left: 10px;
  line-height: 20px;
  white-space: nowrap;
}

.css-nzt1xy {
  display: flex;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  line-height: 20px;
  height: 20px;
}

.css-wh4h37 {
  display: flex;
  margin-right: 4px;
  min-width: 0;
}

.css-1ed8kz2 {
  color: var(--content-basic-disabled, #8d8d95);
  margin-right: 4px;
  flex-shrink: 0;
}

@media (max-width: 768px) {
  .archives-layout {
    flex-direction: column;
  }

  .css-og8kck,
  .css-v8ql05 {
    min-width: 0;
    flex: 1 1 auto;
    width: 100%;
  }

  .archives-main-panel {
    border-left: none;
    border-top: 1px solid var(--border-basic-tertiary, #eeeeef);
  }
}

.missed-chats-override-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  overflow: visible;
  white-space: normal;
  text-overflow: unset;
}

.mc-excluded-badge {
  display: inline-block;
  padding: 1px 6px;
  font-size: 11px;
  font-weight: 600;
  color: rgb(var(--amber-12));
  background-color: rgb(var(--amber-1));
  border: 1px solid rgb(var(--amber-9) / 0.35);
  border-radius: 4px;
  line-height: 1.4;
  flex-shrink: 0;
}

.mc-override-btn {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 4px;
  border: 1px solid;
  cursor: pointer;
  line-height: 1.4;
  transition: background 0.15s;
  flex-shrink: 0;
}

.mc-override-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.mc-override-btn--exclude {
  color: rgb(var(--ruby-12));
  background: rgb(var(--ruby-1));
  border-color: rgb(var(--ruby-9) / 0.35);
}

.mc-override-btn--exclude:hover:not(:disabled) {
  background: rgb(var(--ruby-9) / 0.12);
}

.mc-override-btn--restore {
  color: rgb(var(--teal-12));
  background: rgb(var(--teal-1));
  border-color: rgb(var(--teal-9) / 0.35);
}

.mc-override-btn--restore:hover:not(:disabled) {
  background: rgb(var(--teal-9) / 0.12);
}
</style>
